import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { useDisplayMoney } from "@majourneys/currency";
import { paysByLink } from "@majourneys/shared";
import type { AiMatch, Budget, BudgetItem } from "../api/ai.api";
import { panelStyle } from "../../../shared/ui/card-frame";
import { LayoutLabel } from "./layout-parts";
import { isRestaurantMatch } from "./match-card";
import { SOFT } from "./soft-tokens";
import { kindLabel } from "../lib/kind-label";

const RED = "#B42318";

/** How a kept line is paid, which decides the group it is added up in. */
type Pay = "online" | "arrival";

type Line = {
  item: BudgetItem;
  match: AiMatch;
  /** The whole party's amount, in the display currency, whole. Null: not counted. */
  amount: number | null;
  /** One traveller's amount, as shown beside a party of two or more. */
  each: number | null;
  /** What to print instead of an amount, when there is none to count. */
  note: "free_to_reserve" | "free" | "varies" | "explore" | null;
  pay: Pay;
};

/**
 * Chat kit C14, Budget: what the plan costs through us, in one framed card.
 *
 * Money rules held here, not in the copy:
 *  - every line is a whole number in the guest's currency, and each group's
 *    total is the SUM OF THE SHOWN LINES (one traveller's price converted and
 *    rounded, then times the party), so the column always adds up;
 *  - stays (and the two desert camps, paid from an emailed link) are paid
 *    online and add up on their own; everything else is due on the day, and
 *    the two are never mixed into one figure without saying which is which;
 *  - a restaurant is free to reserve and never counted; an explore place has
 *    no price and is not counted; a suggestion we did not keep is listed
 *    apart and never counted.
 *
 * The bar is a transform (scaleX), never a width.
 */
export function BudgetView({ budget, matches }: { budget: Budget; matches: AiMatch[] }) {
  const { t } = useTranslation();
  const money = useDisplayMoney();
  const bySlug = new Map(matches.map((m) => [m.slug, m]));
  const travelers = Math.max(1, budget.travelers);

  const lines: Line[] = [];
  for (const item of budget.items) {
    const match = bySlug.get(item.matchSlug);
    if (!match) continue;
    const pay: Pay = match.segment === "stays" || paysByLink(match.slug) ? "online" : "arrival";
    let note: Line["note"] = null;
    let each: number | null = null;
    if (match.mode === "explore") note = "explore";
    else if (isRestaurantMatch(match)) note = "free_to_reserve";
    else if (item.priceMad == null) note = "varies";
    else if (item.priceMad === 0) note = "free";
    else each = money.unit(item.priceMad);
    lines.push({ item, match, each, amount: each != null ? each * travelers : null, note, pay });
  }

  const kept = lines.filter((l) => l.item.kept);
  const suggested = lines.filter((l) => !l.item.kept);
  const online = kept.filter((l) => l.pay === "online");
  const arrival = kept.filter((l) => l.pay === "arrival");
  const sum = (ls: Line[]) => ls.reduce((s, l) => s + (l.amount ?? 0), 0);
  const onlineTotal = sum(online);
  const arrivalTotal = sum(arrival);
  const used = onlineTotal + arrivalTotal;

  const cap = money.unit(budget.totalMad);
  const remaining = cap - used;
  const over = remaining < 0;
  const pct = cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0;
  const tight = !over && pct >= 80;
  const statusColor = over ? RED : tight ? SOFT.goldInk : SOFT.ok;

  return (
    <section style={{ ...panelStyle(18, "#fff"), padding: 12 }} data-testid="budget-card">
      <LayoutLabel>
        {t("ai_concierge.budget_header", {
          defaultValue: "Budget · {{travelers}} traveler(s)",
          travelers: budget.travelers,
        })}
        {budget.dailyMad
          ? ` · ${t("ai_concierge.budget_per_day", {
              defaultValue: "{{amount}} a day",
              amount: money.format(money.unit(budget.dailyMad)),
            })}`
          : ""}
      </LayoutLabel>

      <div className="mt-1.5 flex items-end justify-between gap-2">
        <div className="text-[18px] font-semibold leading-none tabular-nums" style={{ color: SOFT.ink }}>
          {money.format(used)}
          <span className="ms-1.5 text-[12px] font-medium" style={{ color: SOFT.body }}>
            / {money.format(cap)}
          </span>
        </div>
        <span
          className="rounded-full px-2.5 py-1 text-[11px] font-semibold"
          style={{ color: statusColor, boxShadow: `0 0 0 1px ${SOFT.controlEdge}`, background: SOFT.controlFill }}
          data-testid="budget-status"
        >
          {over
            ? t("ai_concierge.budget_over_amount", {
                defaultValue: "Over by {{amount}}",
                amount: money.format(Math.abs(remaining)),
              })
            : t("ai_concierge.budget_left_amount", {
                defaultValue: "{{amount}} left",
                amount: money.format(remaining),
              })}
        </span>
      </div>

      <div className="relative mt-2 h-1.5 overflow-hidden rounded-full" style={{ background: SOFT.tint }}>
        <div
          className="absolute inset-0 origin-left rounded-full motion-safe:transition-transform motion-safe:duration-200 rtl:origin-right"
          style={{ transform: `scaleX(${pct / 100})`, background: over ? RED : SOFT.gold }}
        />
      </div>
      <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px]" style={{ color: SOFT.body }}>
        <span>
          {t("ai_concierge.budget_allocated", { defaultValue: "{{pct}}% allocated", pct })}
        </span>
        <span className="text-end">{budget.assumptionsLine}</span>
      </div>

      {online.length > 0 ? (
        <Group
          testId="budget-online"
          title={t("ai_concierge.budget_paid_online", { defaultValue: "Paid online" })}
          lines={online}
          total={onlineTotal}
          travelers={travelers}
        />
      ) : null}
      {arrival.length > 0 ? (
        <Group
          testId="budget-arrival"
          title={t("ai_concierge.budget_due_on_day", { defaultValue: "Due on the day" })}
          lines={arrival}
          total={arrivalTotal}
          travelers={travelers}
        />
      ) : null}

      {online.length > 0 && arrival.length > 0 ? (
        <div
          className="mt-2.5 flex items-baseline justify-between border-t pt-2.5"
          style={{ borderColor: SOFT.hairline }}
          data-testid="budget-total"
        >
          <span className="text-[13px] font-semibold" style={{ color: SOFT.ink }}>
            {t("ai_concierge.budget_total", { defaultValue: "Total" })}
          </span>
          <span className="text-[15px] font-semibold tabular-nums" style={{ color: SOFT.ink }}>
            {money.format(used)}
          </span>
        </div>
      ) : null}

      {suggested.length > 0 ? (
        <div className="mt-3 border-t pt-2.5" style={{ borderColor: SOFT.hairline }} data-testid="budget-suggested">
          <div className="text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: SOFT.body }}>
            {t("ai_concierge.budget_suggested", { defaultValue: "Suggested" })}
          </div>
          <ul className="mt-1 flex flex-col">
            {suggested.map((l) => (
              <Row key={l.item.matchSlug} line={l} travelers={travelers} dim />
            ))}
          </ul>
        </div>
      ) : null}

      {budget.savingsTip ? (
        <div
          className="mt-3 flex items-start gap-2 rounded-[12px] px-3 py-2.5 text-[12px]"
          style={{ background: SOFT.tint, color: SOFT.ink }}
        >
          <Icon name="ri-lightbulb-flash-line" size={14} className="mt-0.5" style={{ color: SOFT.goldInk }} aria-hidden="true" />
          <span>
            <strong>{t("ai_concierge.budget_tip_label", { defaultValue: "Savings tip:" })}</strong>{" "}
            {budget.savingsTip}
          </span>
        </div>
      ) : null}
    </section>
  );
}

function Group({
  title,
  lines,
  total,
  travelers,
  testId,
}: {
  title: string;
  lines: Line[];
  total: number;
  travelers: number;
  testId: string;
}) {
  const money = useDisplayMoney();
  return (
    <div className="mt-3 border-t pt-2.5" style={{ borderColor: SOFT.hairline }} data-testid={testId}>
      <ul className="flex flex-col">
        {lines.map((l) => (
          <Row key={l.item.matchSlug} line={l} travelers={travelers} />
        ))}
      </ul>
      <div className="mt-1.5 flex items-baseline justify-between">
        <span className="text-[12.5px] font-semibold" style={{ color: SOFT.ink }}>
          {title}
        </span>
        <span className="text-[14px] font-semibold tabular-nums" style={{ color: SOFT.ink }} data-testid={`${testId}-total`}>
          {money.format(total)}
        </span>
      </div>
    </div>
  );
}

function Row({ line, travelers, dim = false }: { line: Line; travelers: number; dim?: boolean }) {
  const { t } = useTranslation();
  const money = useDisplayMoney();
  const { match, item, amount, each, note } = line;
  const meta = [kindLabel(t, match), match.cityName].filter(Boolean).join(" · ");
  const noteText =
    note === "free_to_reserve"
      ? t("ai_concierge.free_to_reserve", { defaultValue: "Free to reserve" })
      : note === "free"
        ? t("ai_concierge.budget_free", { defaultValue: "Free" })
        : note === "varies"
          ? t("ai_concierge.budget_unknown_price", { defaultValue: "Varies" })
          : null;

  return (
    <li
      className="flex items-center gap-2.5 py-1.5"
      style={dim ? { opacity: 0.72 } : undefined}
      data-testid={`budget-line-${item.matchSlug}`}
    >
      <span
        className="h-9 w-9 shrink-0 overflow-hidden rounded-[9px] bg-cover bg-center"
        style={{
          background: match.heroImageUrl ? `center / cover no-repeat url(${match.heroImageUrl})` : "#eceaf5",
        }}
        aria-hidden="true"
      />
      <span className="min-w-0 flex-1 text-start">
        <span className="line-clamp-2 block break-words text-[12.5px] font-semibold" style={{ color: SOFT.ink }}>
          {match.title ?? match.slug}
        </span>
        <span className="line-clamp-2 block break-words text-[11px]" style={{ color: SOFT.body }}>
          {meta}
          {item.reason && !item.kept ? ` · ${item.reason}` : ""}
        </span>
      </span>
      <span className="shrink-0 text-end">
        {amount != null ? (
          <>
            <span className="block text-[12.5px] font-semibold tabular-nums" style={{ color: SOFT.ink }}>
              {money.format(amount)}
            </span>
            {travelers > 1 && each != null ? (
              <span className="block text-[10.5px] tabular-nums" style={{ color: SOFT.body }}>
                {t("ai_concierge.budget_per_person", {
                  defaultValue: "{{amount}} per person",
                  amount: money.format(each),
                })}
              </span>
            ) : null}
          </>
        ) : noteText ? (
          <span
            className="block text-[12px] font-semibold"
            style={{ color: note === "varies" ? SOFT.body : SOFT.ok }}
          >
            {noteText}
          </span>
        ) : null}
      </span>
    </li>
  );
}
